import useScheduleRealtime from '../../realtime/useScheduleRealtime';
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import ScheduleJobModal from "./ScheduleJobModal";
import AccountingModal from "./AccountingModal";
import { formatDateLocalEnhanced } from "../../utils/dateUtils";
import { hasAnyEditPermission } from './utils/schedulePermissions';
import { fetchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import SortableTableHeader from "./SortableTableHeader";
import { getNextSortConfig, sortTableRows } from "./utils/tableSorting";
import CompactScheduleSection from "./components/CompactScheduleSection";

const TABLE_COLUMNS = [
  { key: "JobNumber", label: "Job Number", type: "text" },
  { key: "ProjectName", label: "Project Name", type: "text" },
  { key: "ModelNumber", label: "Model Number", type: "text" },
  { key: "OrderDate", label: "Order Date", type: "date" },
  { key: "ProductionRelease", label: "Release To Scheduling", type: "date" },
  { key: "RequestedDeliveryDate", label: "Requested Delivery Date", type: "date" },
  { key: "SalesRep", label: "Sales Rep", type: "text" },
  { key: "State", label: "State", type: "text" },
  { key: "JobStatus", label: "Job Status", type: "text" },
  { key: "PaymentTerms", label: "Payment Terms", type: "text" },
  { key: "DollarValue", label: "Dollar Value", type: "currency" },
  { key: "Notes", label: "Notes", type: "text" },
];

const ReadyToSchedule = ({ filteredJobs = null, canEdit = true }) => {
  const dispatch = useDispatch();
  const jobScheduleDataFromRedux = useSelector((state) => state.jobSchedule.data);
  const jobScheduleData = filteredJobs || jobScheduleDataFromRedux;
  const apiUrls = useSelector(selectApiUrls);
  const [readyToScheduleApiJobs, setReadyToScheduleApiJobs] = useState([]);
  const [loadingReadyJobs, setLoadingReadyJobs] = useState(false);
  const [readyJobsError, setReadyJobsError] = useState(null);
  
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  const hasEditAccess = Boolean(canEdit) && hasAnyEditPermission(userRoles);
  
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  const [accountingJob, setAccountingJob] = useState(null);
  const [visibleRows, setVisibleRows] = useState(50);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const containerRef = useRef(null);

  useScheduleRealtime({ view: 'ready', blocked: loadingReadyJobs || showScheduleModal || showAccountingModal, apply: rows => { setReadyToScheduleApiJobs(rows); setReadyJobsError(null); } });

  useEffect(() => {
    let isMounted = true;

    const loadReadyToScheduleJobs = async () => {
      if (!apiUrls?.fullSchedule) {
        return;
      }

      setLoadingReadyJobs(true);
      setReadyJobsError(null);

      try {
        const queryJoiner = apiUrls.fullSchedule.includes("?") ? "&" : "?";
        const url = `${apiUrls.fullSchedule}${queryJoiner}jobStatus=${encodeURIComponent('Ready To Schedule')}`;
        const response = await dispatch(fetchData({ url })).unwrap();
        const raw = response?.data || response || {};
        const normalizedJobs = Array.isArray(raw)
          ? raw
          : Object.values(raw)
            .map((item) => item?.GeneralProjectInfo)
            .filter(Boolean);

        if (isMounted) {
          setReadyToScheduleApiJobs(normalizedJobs);
        }
      } catch (error) {
        console.error('ReadyToSchedule: Failed to load ready to schedule jobs from full schedule endpoint:', error);
        if (isMounted) {
          setReadyJobsError('Failed to load ready to schedule jobs.');
        }
      } finally {
        if (isMounted) {
          setLoadingReadyJobs(false);
        }
      }
    };

    loadReadyToScheduleJobs();

    return () => {
      isMounted = false;
    };
  }, [dispatch, apiUrls?.fullSchedule]);

  const handleRowDoubleClick = (job) => {
    // Check if user has accounting role and show accounting modal
    if (userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling) {
      setAccountingJob(job);
      setShowAccountingModal(true);
      return;
    }
    
    // Only allow double-click functionality if user has edit permissions
    if (hasEditAccess) {
      setSelectedJob(job);
      setShowScheduleModal(true);
    }
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
    setSelectedJob(null);
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setVisibleRows((prev) => prev + 50);
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Use API jobs when available (already filtered by backend), otherwise fall back to filtered Redux data
  const fallbackJobs = Array.isArray(jobScheduleData)
    ? jobScheduleData.filter(
        (job) =>
          (job.JobStatus === "Ready To Schedule" ||
            job.JobStatus === "Ready to Schedule") &&
          job.ActualShipDate == null
      )
    : [];

  const allJobs = readyToScheduleApiJobs.length > 0 
    ? readyToScheduleApiJobs
    : fallbackJobs;

  const shouldShowReadyJobsError = Boolean(readyJobsError) && allJobs.length === 0;
  const sortedJobs = useMemo(
    () => sortTableRows(allJobs, TABLE_COLUMNS, sortConfig),
    [allJobs, sortConfig]
  );

  const handleSort = (key) => {
    setSortConfig((currentSort) => getNextSortConfig(currentSort, key));
    setVisibleRows(50);
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    const numValue = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  const compactFields = [
    { key: "ProjectName", label: "Project Name", className: "compact-inline-project-column" },
    { key: "ModelNumber", label: "Model Number", className: "compact-inline-model-column" },
    { key: "OrderDate", label: "Order Date", render: (job) => formatDateLocalEnhanced(job.OrderDate) },
    { key: "ProductionRelease", label: "Production Release", render: (job) => formatDateLocalEnhanced(job.ProductionRelease) },
    { key: "SalesRep", label: "Sales Rep" },
    { key: "State", label: "State" },
    { key: "JobStatus", label: "Job Status" },
    { key: "PaymentTerms", label: "Payment Terms" },
    { key: "DollarValue", label: "Dollar Value", render: (job) => formatCurrency(job.DollarValue) },
    { key: "Notes", label: "Notes", className: "notes-column" },
  ];

  // Slice data for visible rows
  const visibleJobs = sortedJobs.slice(0, visibleRows);

  const renderJobTable = (data) => (
    <>
      <div className="schedule-simple-desktop-table">
        <Table striped bordered hover responsive className="schedule-responsive-table">
      <SortableTableHeader
        columns={TABLE_COLUMNS}
        sortConfig={sortConfig}
        onSort={handleSort}
      />
      <tbody>
        {data.length > 0 ? (
          data.map((job) => (
            <tr
              key={job.ID}
              className="schedule-collapsible-row"
              onDoubleClick={() => handleRowDoubleClick(job)}
              style={{ cursor: (hasEditAccess || (userRolesMapped && userRolesMapped.Accounting)) ? 'pointer' : 'default' }}
              title={(hasEditAccess || (userRolesMapped && userRolesMapped.Accounting)) ? "Double-click to edit" : ""}
            >
              <td data-label="Job Number" className="schedule-card-primary">{job.JobNumber}</td>
              <td data-label="Project Name" className="schedule-card-primary">{job.ProjectName}</td>
              <td data-label="Model Number" className="schedule-card-primary">{job.ModelNumber}</td>
              <td data-label="Order Date" className="schedule-card-secondary">{formatDateLocalEnhanced(job.OrderDate)}</td>
              <td data-label="Production Release" className="schedule-card-secondary">{formatDateLocalEnhanced(job.ProductionRelease)}</td>
              <td data-label="Requested Delivery" className="schedule-card-secondary">{formatDateLocalEnhanced(job.RequestedDeliveryDate)}</td>
              <td data-label="Sales Rep" className="schedule-card-secondary">{job.SalesRep}</td>
              <td data-label="State" className="schedule-card-secondary">{job.State}</td>
              <td data-label="Status" className="schedule-card-primary">{job.JobStatus}</td>
              <td data-label="Payment Terms" className="schedule-card-secondary">{job.PaymentTerms}</td>
              <td data-label="Dollar Value" className="schedule-card-secondary" style={{ textAlign: 'right' }}>{formatCurrency(job.DollarValue)}</td>
              <td data-label="Notes" className="schedule-card-secondary">{job.Notes}</td>
              {(hasEditAccess || (userRolesMapped && userRolesMapped.Accounting)) && (
                <td data-label="Actions" className="schedule-card-action-cell">
                  <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleRowDoubleClick(job)}>
                    Open job
                  </button>
                </td>
              )}
            </tr>
          ))        ) : (
          <tr>
            <td colSpan="12" className="schedule-empty-row">No data available</td>
          </tr>
        )}
      </tbody>
        </Table>
      </div>
      <div className="schedule-simple-compact-list">
        <CompactScheduleSection
          title={`Ready to Schedule (${allJobs.length})`}
          jobs={data}
          dateLabel="Requested Delivery"
          getDateValue={(job) => formatDateLocalEnhanced(job.RequestedDeliveryDate)}
          fields={compactFields}
        />
      </div>
    </>
  );

  return (
    <div
      className="ready-to-schedule-container"
      style={{ overflow: "auto", maxHeight: "80vh" }}
      ref={containerRef}
    >
      <h4 className="schedule-simple-desktop-only">Ready to Schedule ({allJobs.length})</h4>
      
      {loadingReadyJobs && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading ready to schedule jobs...</span>
          </Spinner>
          <p>Loading ready to schedule jobs...</p>
        </div>
      )}

      {shouldShowReadyJobsError && (
        <Alert variant="danger">
          {readyJobsError}
        </Alert>
      )}

      {!loadingReadyJobs && renderJobTable(visibleJobs)}
      {visibleRows < sortedJobs.length && (
        <div style={{ textAlign: "center", padding: "10px" }}>
          Loading more rows...
        </div>
      )}

      {/* ScheduleJobModal for all jobs in this component */}
      <Modal 
        className="job-schedule-modal"
        show={showScheduleModal} 
        onHide={() => {
          // Get reference to the modal component
          const modalComponent = document.querySelector(".modal-content")
            .closest(".modal");
          
          // Dispatch a click event on the ScheduleJobModal's internal close handler
          // which will trigger the confirmation dialog if there are unsaved changes
          const closeButton = modalComponent.querySelector(".modal-header .btn-close");
          if (closeButton) {
            closeButton.click();
          } else {
            // Fallback if button not found
            handleCloseScheduleModal();
          }
        }} 
        size="xl"
      >
        <ScheduleJobModal
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          handleCloseModal={handleCloseScheduleModal}
          handleSaveChanges={() => {}}
          canEdit={hasEditAccess}
        />
      </Modal>

      {/* Accounting Modal for accounting users */}
      <AccountingModal
        show={showAccountingModal}
        onHide={() => setShowAccountingModal(false)}
        job={accountingJob}
      />
    </div>
  );
};

export default ReadyToSchedule;
